import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { adminApi, publicApi } from '@/lib/api';
import { youtubeEmbedUrl } from '@/lib/taxonomies';
import { ImageUploader, absoluteImageUrl } from '@/components/admin/ImageUploader';
import { ADMIN } from '@/constants/testIds';
import { useSeo } from '@/lib/seo';
import { PlayCircle } from 'lucide-react';

const EMPTY_COVER = { url: '', alt: '' };

export default function FeaturedVideoPage() {
  useSeo({ title: 'Featured Video | Admin' });
  const [form, setForm] = useState({
    youtube_url: '',
    title: '',
    description: '',
    cover_image: EMPTY_COVER,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    publicApi.featuredVideo().then((d) => {
      if (d) {
        setForm({
          youtube_url: d.youtube_url || '',
          title: d.title || '',
          description: d.description || '',
          cover_image: d.cover_image || EMPTY_COVER,
        });
      }
    });
  }, []);

  const embed = form.youtube_url ? youtubeEmbedUrl(form.youtube_url) : null;
  const coverPreview = form.cover_image?.url ? absoluteImageUrl(form.cover_image.url) : null;

  const save = async (e) => {
    e.preventDefault();
    if (!form.youtube_url || !form.title) {
      toast.error('YouTube URL and title are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.cover_image?.url) payload.cover_image = null;
      await adminApi.updateVideo(payload);
      toast.success('Featured video updated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6 max-w-3xl">
      <div>
        <p className="kicker">Featured Video</p>
        <h1 className="font-serif text-3xl tracking-tight">The video shown on the home page.</h1>
      </div>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6 grid gap-5">
          <div>
            <Label className="kicker mb-2 block">YouTube URL</Label>
            <Input
              value={form.youtube_url}
              onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
              placeholder="https://youtu.be/…"
            />
          </div>
          <div>
            <Label className="kicker mb-2 block">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <Label className="kicker mb-2 block">Description</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6">
          <ImageUploader
            label="Cover image (recommended)"
            value={form.cover_image}
            onChange={(v) => setForm({ ...form, cover_image: v })}
          />
          <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] max-w-lg">
            Shown as a click-to-play still on the home page. This keeps the video card on-brand and visible
            even when a visitor’s network blocks YouTube previews. If left empty, the home page falls back to the
            YouTube embed directly.
          </p>
        </CardContent>
      </Card>

      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6 space-y-3">
          <p className="kicker">Preview — how visitors will see it</p>
          <div className="rounded-lg overflow-hidden border border-[hsl(var(--border))]">
            <AspectRatio ratio={16 / 9}>
              {coverPreview ? (
                <div className="relative h-full w-full">
                  <img
                    src={coverPreview}
                    alt={form.cover_image?.alt || form.title}
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-14 w-14 rounded-full bg-white/90 flex items-center justify-center">
                      <PlayCircle size={38} className="text-[hsl(var(--primary))]" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <p className="text-xs uppercase tracking-wider opacity-80">Latest video</p>
                    <p className="font-serif text-lg leading-tight line-clamp-2">{form.title || 'Untitled'}</p>
                  </div>
                </div>
              ) : embed ? (
                <iframe src={embed} title="Preview" className="h-full w-full" allowFullScreen />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-[hsl(var(--secondary))] text-sm text-[hsl(var(--muted-foreground))]">
                  Add a YouTube URL or cover image to preview.
                </div>
              )}
            </AspectRatio>
          </div>
        </CardContent>
      </Card>

      <div>
        <Button type="submit" disabled={saving} data-testid={ADMIN.videoSave}>
          {saving ? 'Saving…' : 'Save video'}
        </Button>
      </div>
    </form>
  );
}
